import { AgentWorkflowDefinition, Edge, Node } from "@/store/agentsStore";
import { fetchApi } from "@/lib/api";
import { z } from "zod";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isPosition = (value: unknown): value is { x: number; y: number } =>
  isRecord(value) &&
  typeof value.x === "number" &&
  Number.isFinite(value.x) &&
  typeof value.y === "number" &&
  Number.isFinite(value.y);

const isNode = (value: unknown): value is Node => {
  if (!isRecord(value)) {
    return false;
  }

  if (
    typeof value.id !== "string" ||
    typeof value.type !== "string" ||
    !isPosition(value.position)
  ) {
    return false;
  }

  if (value.type === "TRIGGER") {
    return Array.isArray(value.triggerConfiguration);
  }

  return Array.isArray(value.actionConfiguration);
};

const isEdge = (value: unknown): value is Edge => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === "string" &&
    typeof value.condition === "string" &&
    typeof value.from === "string" &&
    typeof value.to === "string"
  );
};

const isAgentWorkflowDefinition = (
  value: unknown,
): value is AgentWorkflowDefinition => {
  if (!isRecord(value)) {
    return false;
  }

  const { nodes, edges } = value;

  return (
    Array.isArray(nodes) &&
    nodes.every(isNode) &&
    Array.isArray(edges) &&
    edges.every(isEdge)
  );
};

const agentWorkflowExportPayloadSchema: z.ZodType<AgentWorkflowExportPayload> =
  z.object({
    definition: z.custom<AgentWorkflowDefinition>(isAgentWorkflowDefinition, {
      message: "definition must include nodes and edges arrays",
    }),
    prompt: z.string().nullable().optional().default(null),
    timezone: z.string().min(1, "timezone is required"),
    loadRules: z.boolean().optional().default(false),
  });

export type AgentWorkflowExportPayload = {
  definition: AgentWorkflowDefinition;
  prompt: string | null;
  timezone: string;
  loadRules: boolean;
};

/**
 * Parses and validates an exported agent workflow JSON string via a Zod schema
 * so we get descriptive validation errors and future-friendly checks.
 *
 * @param raw - The raw JSON string to parse
 * @returns The parsed payload if valid, null otherwise
 */
export const parseExportPayload = (
  raw: string,
): AgentWorkflowExportPayload | null => {
  try {
    return agentWorkflowExportPayloadSchema.parse(JSON.parse(raw));
  } catch {
    return null;
  }
};

/**
 * Checks if a workflow has existing content (nodes, edges, or prompt).
 *
 * @param definition - The workflow definition to check
 * @param prompt - The workflow prompt to check
 * @returns True if there is existing content, false otherwise
 */
export const hasWorkflowContent = (
  definition: AgentWorkflowDefinition | null | undefined,
  prompt: string | null | undefined,
): boolean => {
  if (!definition) {
    return false;
  }

  const hasNodesOrEdges =
    definition.nodes.length > 0 || definition.edges.length > 0;
  const hasPrompt = Boolean(prompt);

  return hasNodesOrEdges || hasPrompt;
};

/**
 * Exports an agent workflow by fetching it from the API.
 *
 * @param agentId - The ID of the agent to export
 * @returns The export payload and JSON string if successful, null otherwise
 */
export const exportAgentWorkflow = async (
  agentId: string,
): Promise<{
  payload: AgentWorkflowExportPayload;
  jsonString: string;
} | null> => {
  const response = await fetchApi(`agents/workflows/${agentId}/export`, "GET");

  if (!response.ok || !response.data) {
    return null;
  }

  const payload = response.data as AgentWorkflowExportPayload;
  const jsonString = JSON.stringify(payload, null, 2);

  return { payload, jsonString };
};

/**
 * Copies text to the clipboard.
 *
 * @param text - The text to copy
 * @returns True if successful, false otherwise
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }
  return false;
};
